import type { TFunction } from "i18next";
import type { AppNotification } from "@/lib/api";
import i18n from "@/i18n";
import { formatLocalizedDate } from "@/lib/localized-date";
import { getLeaveTypeLabel } from "@/lib/leave-i18n";

function parseNotificationPayload(message: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(message);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : null;
  } catch {
    return null;
  }
}

function payloadText(
  payload: Record<string, unknown> | null,
  keys: string[],
): string {
  if (!payload) return "";
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return "";
}

function payloadKey(payload: Record<string, unknown> | null): string {
  return payloadText(payload, ["notificationKey", "i18nKey", "key"]);
}

const STATUS_KEY_BY_VALUE: Record<string, string> = {
  resolved: "resolved",
  review: "review",
  "in review": "review",
  closed: "closed",
  approved: "approved",
  rejected: "rejected",
  pending: "pending",
  submitted: "pending",
  assigned: "review",
  "pending approval": "pending",
};

function normalizeStatusValue(status: string): string {
  return status.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
}

function getLocalizedStatus(status: string, t: TFunction): string {
  const normalized = normalizeStatusValue(status);
  const statusKey = STATUS_KEY_BY_VALUE[normalized];
  if (!statusKey) return status;

  return t(`notifications.statusValues.${statusKey}`, normalized);
}

function getLocalizedCurrency(currency: string, t: TFunction): string {
  const normalized = currency.trim().toUpperCase();
  if (!normalized) return currency;

  return t(`notifications.currency.${normalized}`, normalized);
}

function extractConcernStatus(notification: AppNotification, payload: Record<string, unknown> | null): string {
  const fromPayload = payloadText(payload, ["status", "newStatus", "statusValue"]);
  if (fromPayload) return fromPayload;

  const match = notification.message.match(/updated to:\s*([^.!?]+)\.?$/i);
  return match?.[1]?.trim() ?? "";
}

function isConcernReviewRequested(
  notification: AppNotification,
  payload: Record<string, unknown> | null,
): boolean {
  if (payloadKey(payload) === "concernReviewRequested") return true;

  return notification.entityType === "concern_case"
    && notification.type === "concern_new"
    && (
      notification.title === "Review Requested on Concern Case"
      || /anonymous submitter asked HR to review/i.test(notification.message)
    );
}

function extractExpenseApprovedDetails(
  notification: AppNotification,
  payload: Record<string, unknown> | null,
): { expenseName: string; amount: string; currency: string } {
  const titleMatch = notification.title.match(/^Expense Approved(?:\s+[—–-]\s+(.+))?$/i);
  const messageMatch = notification.message.match(
    /^Your expense\s+(?:"|“|„)(.*?)(?:"|”|“)\s*(?:for\s+(.+?)\s+([A-Z]{3})\s+)?has been approved\.?$/i,
  );

  return {
    expenseName: payloadText(payload, ["expenseName", "expenseTitle", "title", "name"])
      || titleMatch?.[1]?.trim()
      || messageMatch?.[1]?.trim()
      || "",
    amount: payloadText(payload, ["amountFormatted", "formattedAmount", "amount"])
      || messageMatch?.[2]?.trim()
      || "",
    currency: payloadText(payload, ["currency"])
      || messageMatch?.[3]?.trim()
      || "",
  };
}

function isExpenseApproved(
  notification: AppNotification,
  payload: Record<string, unknown> | null,
): boolean {
  if (payloadKey(payload) === "expenseApproved") return true;
  if (notification.type === "expense_approved") return true;

  return notification.entityType === "workflow"
    && /^Expense Approved\b/i.test(notification.title);
}

function isLeaveReplacementNotification(
  notification: AppNotification,
  key: string,
): boolean {
  return notification.type === "leave_replacement"
    || key === "leaveReplacementAssigned"
    || key === "leaveReplacementApproved";
}

const MEDICAL_CHECK_ASSIGNMENT_NOTIFICATION_TYPES = new Set([
  "medical_check_assigned",
  "medical_check_rejected",
  "medical_check_accepted",
  "medical_check_completed",
  "medical_check_confirmed",
  "medical_check_result",
  "medical_check_rescheduled",
  "medical_check_cancelled",
]);

export function isMedicalCheckAssignmentNotification(type: string): boolean {
  return MEDICAL_CHECK_ASSIGNMENT_NOTIFICATION_TYPES.has(type);
}

export function getNotificationDisplayText(
  notification: AppNotification,
  t: TFunction,
): { title: string; message: string } {
  const payload = parseNotificationPayload(notification.message);
  const key = payloadKey(payload);

  const leaveTypeKey = notification.type === "employee_reactivated" && key === "leaveReactivated" ? "leave_reactivated" : notification.type;
  const leaveTypes = ["leave_submitted", "leave_approved", "leave_rejected", "leave_cancelled", "leave_reminder", "leave_created_on_behalf", "leave_document_uploaded", "leave_reactivated"];
  if (leaveTypes.includes(leaveTypeKey)) {
    const employeeName = payloadText(payload, ["employeeName"])
      || notification.title.match(/^Leave request from (.+)$/i)?.[1]
      || notification.message.match(/^(.+?) (?:has submitted|has cancelled|uploaded supporting)/i)?.[1] || "";
    const rawLabel = payloadText(payload, ["leaveTypeLabel", "leaveType"])
      || notification.message.match(/^Your (.+?) request \(/i)?.[1]
      || notification.message.match(/has submitted a (.+?) request \(/i)?.[1]
      || notification.message.match(/created (.+?) for you \(/i)?.[1] || "";
    // Legacy rows contain an English label rather than a stable type key. Use
    // the existing translations to recognize every built-in type, not just annual/sick.
    const englishLabels = t("leave.types", { lng: "en", returnObjects: true }) as unknown as Record<string, string>;
    const typeCode = payloadText(payload, ["leaveType"])
      || Object.entries(englishLabels).find(([code, label]) => code === rawLabel.toLowerCase()
        || (typeof label === "string" && label.toLowerCase() === rawLabel.toLowerCase()))?.[0]
      || rawLabel;
    const leaveType = getLeaveTypeLabel(t, typeCode, rawLabel);
    const dates = notification.message.match(/\(([^()]+?)\s+[–-]\s+([^()]+?)\)/);
    const isoDates = notification.message.match(/\d{4}-\d{2}-\d{2}/g) ?? [];
    const startDate = payloadText(payload, ["startDate"]) || isoDates[0] || dates?.[1] || "";
    const endDate = payloadText(payload, ["endDate"]) || isoDates[1] || dates?.[2]?.split(",")[0] || "";
    const language = (t as TFunction & { lng?: string }).lng || i18n.resolvedLanguage || i18n.language;
    const date = (value: string) => /^\d{4}-\d{2}-\d{2}/.test(value)
      ? formatLocalizedDate(value.slice(0, 10), language) : value;
    const localizedLeaveType = leaveType === getLeaveTypeLabel(t, typeCode)
      ? leaveType.charAt(0).toLocaleLowerCase() + leaveType.slice(1) : leaveType;
    const values = {
      employeeName, leaveType: localizedLeaveType, startDate: date(startDate), endDate: date(endDate),
      reason: payloadText(payload, ["rejectionReason", "reason"]) || notification.message.match(/Reason:\s*(.+)$/i)?.[1] || "",
      creatorName: payloadText(payload, ["creatorName"]) || notification.message.match(/^(.+?) created /)?.[1] || "HR",
      documentLabel: payloadText(payload, ["documentLabel"]),
    };
    return {
      title: t(`notifications.leave.${leaveTypeKey}.title`, values),
      message: t(`notifications.leave.${leaveTypeKey}.message`, values),
    };
  }

  if (notification.type === "onboarding_task_assigned" || key === "onboardingTaskAssigned") {
    const employeeName = payloadText(payload, ["employeeName"]);
    const taskTitle = payloadText(payload, ["taskTitle"]);
    return {
      title: t("notifications.onboardingTaskAssignedTitle"),
      message: t("notifications.onboardingTaskAssignedMessage", { employeeName, taskTitle }),
    };
  }

  if (notification.entityType === "medical_check" || notification.type.startsWith("medical_check_")) {
    const scheduledDate = payloadText(payload, ["scheduledDate", "date", "nextDueDate"])
      || notification.message.match(/\b(\d{4}-\d{2}-\d{2})\b/)?.[1]
      || "";
    const scheduledTime = (payloadText(payload, ["scheduledTime", "time"])
      || notification.message.match(/\bat\s+(\d{2}:\d{2})\b/i)?.[1]
      || "").slice(0, 5);
    const institution = payloadText(payload, ["institution"])
      || notification.message.match(/\b(?:at|во)\s+(?:\d{2}:\d{2}\s+(?:at|во)\s+)?([^.!?]+)[.!?]/i)?.[1]?.trim()
      || "";
    const employeeName = payloadText(payload, ["employeeName"])
      || notification.title.match(/(?:for|by)\s+(.+)$/i)?.[1]?.trim()
      || notification.title.match(/^(.+?)\s+accepted\s+medical\s+check/i)?.[1]?.trim()
      || notification.message.match(/^(.+?)\s+completed\s+the\s+medical\s+check/i)?.[1]?.trim()
      || "";
    const rejectionReason = payloadText(payload, ["rejectionReason", "reason"])
      || notification.message.match(/Reason:\s*(.+)$/i)?.[1]?.trim()
      || "";
    const rawResult = payloadText(payload, ["result"])
      || (/failed/i.test(`${notification.title} ${notification.message}`) ? "failed" : "passed");
    const result = t(`medicalCheckups.assignmentStatus.${rawResult}`, rawResult);
    const date = scheduledDate
      ? formatLocalizedDate(scheduledDate, i18n.resolvedLanguage || i18n.language)
      : "";
    const previousScheduledDate = payloadText(payload, ["previousScheduledDate", "oldScheduledDate", "previousDate"]);
    const previousDate = previousScheduledDate
      ? formatLocalizedDate(previousScheduledDate, i18n.resolvedLanguage || i18n.language)
      : "";
    const previousTime = payloadText(payload, ["previousScheduledTime", "oldScheduledTime", "previousTime"]).slice(0, 5);
    const previousInstitution = payloadText(payload, ["previousInstitution", "oldInstitution"]);
    const when = [date, scheduledTime].filter(Boolean).join(" · ");
    const vars = {
      date,
      time: scheduledTime,
      when,
      institution,
      previousDate,
      previousTime,
      previousInstitution,
      employeeName,
      rejectionReason,
      result,
    };

    switch (notification.type) {
      case "medical_check_assigned":
        return {
          title: t("notifications.medicalCheck.assignedTitle"),
          message: t("notifications.medicalCheck.assignedMessage", vars),
        };
      case "medical_check_rejected":
        return {
          title: t("notifications.medicalCheck.rejectedTitle", vars),
          message: t("notifications.medicalCheck.rejectedMessage", vars),
        };
      case "medical_check_accepted":
        return {
          title: t("notifications.medicalCheck.acceptedTitle", vars),
          message: t("notifications.medicalCheck.acceptedMessage", vars),
        };
      case "medical_check_completed":
      case "medical_check_confirmed":
        return {
          title: t("notifications.medicalCheck.completedTitle", vars),
          message: t(institution
            ? "notifications.medicalCheck.completedMessage"
            : "notifications.medicalCheck.completedMessageNoInstitution", vars),
        };
      case "medical_check_result":
        return {
          title: t("notifications.medicalCheck.resultTitle", vars),
          message: t(institution
            ? "notifications.medicalCheck.resultMessage"
            : "notifications.medicalCheck.resultMessageNoInstitution", vars),
        };
      case "medical_check_rescheduled":
        return {
          title: t("notifications.medicalCheck.rescheduledTitle"),
          message: previousDate
            ? t(
                previousInstitution && previousInstitution !== institution
                  ? "notifications.medicalCheck.rescheduledMessageWithInstitution"
                  : "notifications.medicalCheck.rescheduledMessage",
                vars,
              )
            : t("notifications.medicalCheck.rescheduledMessageLegacy", vars),
        };
      case "medical_check_cancelled":
        return {
          title: t("notifications.medicalCheck.cancelledTitle"),
          message: t("notifications.medicalCheck.cancelledMessage", vars),
        };
      case "medical_check_due_soon":
        return {
          title: t("notifications.medicalCheck.dueSoonTitle"),
          message: t("notifications.medicalCheck.dueSoonMessage", { date }),
        };
      case "medical_check_expired":
        return {
          title: t("notifications.medicalCheck.expiredTitle"),
          message: t("notifications.medicalCheck.expiredMessage"),
        };
      case "medical_check_hr_digest": {
        const dueCount = payloadText(payload, ["dueCount"]);
        const expiredCount = payloadText(payload, ["expiredCount"]);
        const leadMonths = payloadText(payload, ["leadMonths"]);
        return {
          title: t("notifications.medicalCheck.digestTitle"),
          message: t("notifications.medicalCheck.digestMessage", { dueCount, expiredCount, leadMonths }),
        };
      }
    }
  }

  if (["timesheet_approved", "timesheet_rejected", "timesheet_returned", "timesheet_reminder"].includes(notification.type)) {
    const weekStart = payloadText(payload, ["weekStartDate", "weekStart"])
      || notification.message.match(/week\s+(\d{4}-\d{2}-\d{2})/i)?.[1]
      || "";
    const localizedWeek = weekStart
      ? formatLocalizedDate(weekStart, i18n.resolvedLanguage || i18n.language)
      : "";
    const typeKey = {
      timesheet_approved: "approved",
      timesheet_rejected: "rejected",
      timesheet_returned: "returned",
      timesheet_reminder: "reminder",
    }[notification.type];

    return {
      title: t(`timekeeping.notifications.${typeKey}Title`, notification.title),
      message: localizedWeek
        ? t(`timekeeping.notifications.${typeKey}Message`, {
            week: localizedWeek,
            defaultValue: notification.message,
          })
        : t(`timekeeping.notifications.${typeKey}MessageGeneric`, notification.message),
    };
  }

  if (notification.type === "onboarding_started") {
    return {
      title: t("notifications.onboardingStartedTitle", "Your onboarding has started"),
      message: t(
        "notifications.onboardingStartedMessage",
        "Welcome! Your onboarding process has been set up. Go to My Onboarding to see your tasks and get started.",
      ),
    };
  }

  if (notification.type === "recruitment_application_received") {
    if (!payload) {
      return {
        title: t("notifications.recruitmentApplicationReceived", "New recruitment application"),
        message: notification.message,
      };
    }

    const candidateName = String(payload?.candidateName ?? "");
    const positionTitle = String(payload?.positionTitle ?? "");

    return {
      title: t("notifications.recruitmentApplicationReceived", "New recruitment application"),
      message: t("notifications.recruitmentApplicationReceivedMessage", {
        candidateName: candidateName || t("recruitment.applications.candidate", "Candidate"),
        positionTitle: positionTitle || t("recruitment.applications.position", "position"),
      }),
    };
  }

  if (notification.type === "recruitment_interview" || key === "recruitmentInterviewAssigned") {
    const candidateName = payloadText(payload, ["candidateName"])
      || notification.title.match(/^Schedule Interview:\s*(.+)$/i)?.[1]?.trim()
      || "";
    const positionTitle = payloadText(payload, ["positionTitle"])
      || notification.message.match(/\bfor\s+(.+?)(?:\s+position)?\.?$/i)?.[1]?.trim()
      || "";

    return {
      title: t("notifications.recruitmentInterviewAssignedTitle", { candidateName }),
      message: t("notifications.recruitmentInterviewAssignedMessage", { candidateName, positionTitle }),
    };
  }

  if (notification.type === "interview_today" || key === "recruitmentInterviewToday") {
    const titleMatch = notification.title.match(/^Interview Today:\s*(.+?)\s+[—–-]\s+(.+)$/i);
    const candidateName = payloadText(payload, ["candidateName"])
      || titleMatch?.[1]?.trim()
      || "";
    const positionTitle = payloadText(payload, ["positionTitle"])
      || titleMatch?.[2]?.trim()
      || "";
    const hasMeetingLink = payload?.hasMeetingLink === true
      || /meeting link is ready/i.test(notification.message);

    return {
      title: t("notifications.recruitmentInterviewTodayTitle", { candidateName, positionTitle }),
      message: t(
        hasMeetingLink
          ? "notifications.recruitmentInterviewTodayMessageWithLink"
          : "notifications.recruitmentInterviewTodayMessage",
        { candidateName, positionTitle },
      ),
    };
  }

  if (notification.type === "recruitment_feedback" || key === "recruitmentInterviewReview") {
    const candidateName = payloadText(payload, ["candidateName"])
      || notification.title.match(/^Review Interview:\s*(.+)$/i)?.[1]?.trim()
      || "";
    const positionTitle = payloadText(payload, ["positionTitle"])
      || notification.message.match(/\bfor\s+(.+?)\.\s+Outcome:/i)?.[1]?.trim()
      || "";
    const interviewerName = payloadText(payload, ["interviewerName"])
      || notification.message.match(/^(.+?)\s+completed the interview/i)?.[1]?.trim()
      || "";
    const legacyOutcome = notification.message.match(/Outcome:\s*(?:✅|❌|📋)?\s*(Passed|Failed|Reviewed)/i)?.[1]?.toLowerCase();
    const outcome = payloadText(payload, ["outcome"]) || legacyOutcome || "reviewed";

    return {
      title: t("notifications.recruitmentInterviewReviewTitle", { candidateName }),
      message: t("notifications.recruitmentInterviewReviewMessage", {
        candidateName,
        positionTitle,
        interviewerName,
        outcome: t(`myTasks.interviewTasks.outcomes.${outcome}`),
      }),
    };
  }

  if (isLeaveReplacementNotification(notification, key)) {
    if (!payload) {
      return {
        title: t("notifications.leaveReplacementTitle", {
          defaultValue: notification.title || "Replacement cover",
        }),
        message: notification.message,
      };
    }

    const employeeName = payloadText(payload, ["employeeName", "requesterName"]);
    const leaveType = payloadText(payload, ["leaveTypeLabel", "leaveType"]);
    const startDate = payloadText(payload, ["startDate"]);
    const endDate = payloadText(payload, ["endDate"]);
    const isApproved = key === "leaveReplacementApproved";

    return {
      title: t(
        isApproved
          ? "notifications.leaveReplacementApprovedTitle"
          : "notifications.leaveReplacementAssignedTitle",
        isApproved
          ? "Replacement cover approved"
          : "Replacement cover",
      ),
      message: t(
        isApproved
          ? "notifications.leaveReplacementApprovedMessage"
          : "notifications.leaveReplacementAssignedMessage",
        {
          employeeName,
          leaveType,
          startDate,
          endDate,
          defaultValue: isApproved
            ? "The leave request you are covering for {{employeeName}} has been approved ({{startDate}} - {{endDate}})."
            : "{{employeeName}} selected you as replacement during their {{leaveType}} ({{startDate}} - {{endDate}}).",
        },
      ),
    };
  }

  if (notification.type === "concern_new" && key === "anonymousConcernSubmitted") {
    return {
      title: t("notifications.anonymousConcernSubmittedTitle"),
      message: t("notifications.anonymousConcernSubmittedMessage"),
    };
  }

  if (notification.type === "document_uploaded" && (
    key === "documentUploadedToProfile" || key === "documentVersionUploadedForReview"
  )) {
    const documentName = payloadText(payload, ["documentName"]);
    const employeeName = payloadText(payload, ["employeeName"]);
    return {
      title: t("notifications.documentUploaded"),
      message: t(
        key === "documentVersionUploadedForReview"
          ? "notifications.documentVersionUploadedForReviewMessage"
          : "notifications.documentUploadedToProfileMessage",
        { employeeName, documentName },
      ),
    };
  }

  if (notification.type === "document_request" && key === "documentRequested") {
    const requestedByName = payloadText(payload, ["requestedByName"]) || "HR";
    const documentName = payloadText(payload, ["documentName"]);
    return {
      title: t("notifications.documentRequestedTitle"),
      message: t("notifications.documentRequestedMessage", { requestedByName, documentName }),
    };
  }

  if (notification.type === "document_request" && key === "documentsAssigned") {
    const requestedByName = payloadText(payload, ["requestedByName"]) || "HR";
    const documentName = payloadText(payload, ["documentName"]);
    const count = Number(payloadText(payload, ["documentCount"])) || 1;
    return {
      title: t("notifications.documentsAssignedTitle", { count }),
      message: t("notifications.documentsAssignedMessage", {
        count,
        requestedByName,
        documentName,
      }),
    };
  }

  if (notification.entityType === "equipment" && [
    "equipmentAssigned",
    "equipmentUnassigned",
    "equipmentReturned",
  ].includes(key)) {
    const itemName = payloadText(payload, ["itemName"]);
    const suffix = key === "equipmentAssigned"
      ? "Assigned"
      : key === "equipmentReturned"
        ? "Returned"
        : "Unassigned";
    return {
      title: t(`notifications.equipment.${suffix.toLowerCase()}Title`),
      message: t(`notifications.equipment.${suffix.toLowerCase()}Message`, { itemName }),
    };
  }

  if (isConcernReviewRequested(notification, payload)) {
    return {
      title: t("notifications.concernReviewRequestedTitle", "Review Requested on Concern Case"),
      message: t(
        "notifications.concernReviewRequestedMessage",
        "An anonymous submitter asked HR to review a resolved or closed concern again.",
      ),
    };
  }

  if (notification.type === "concern_status_changed" || key === "concernStatusChanged") {
    const status = extractConcernStatus(notification, payload);

    return {
      title: t("notifications.concernStatusChangedTitle", "Your Concern Status Updated"),
      message: t("notifications.concernStatusChangedMessage", {
        status: status ? getLocalizedStatus(status, t) : status,
        defaultValue: "The status of your anonymous concern has been updated to: {{status}}.",
      }),
    };
  }

  if (isExpenseApproved(notification, payload)) {
    const { expenseName, amount, currency } = extractExpenseApprovedDetails(notification, payload);
    const currencyLabel = currency ? getLocalizedCurrency(currency, t) : "";

    const title = expenseName
      ? t("notifications.expenseApprovedTitleWithName", {
          expenseName,
          defaultValue: "Expense Approved — {{expenseName}}",
        })
      : t("notifications.expenseApprovedTitle", "Expense Approved");

    if (expenseName && amount && currencyLabel) {
      return {
        title,
        message: t("notifications.expenseApprovedMessage", {
          expenseName,
          amount,
          currency: currencyLabel,
          defaultValue: "Your expense \"{{expenseName}}\" for {{amount}} {{currency}} has been approved.",
        }),
      };
    }

    if (expenseName) {
      return {
        title,
        message: t("notifications.expenseApprovedMessageNoAmount", {
          expenseName,
          defaultValue: "Your expense \"{{expenseName}}\" has been approved.",
        }),
      };
    }

    return {
      title,
      message: t("notifications.expenseApprovedGenericMessage", "Your expense has been approved."),
    };
  }

  return {
    title: notification.title,
    message: notification.message,
  };
}
